import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import QRCode from 'qrcode';
import { TWO_FACTOR_SETUP_STRINGS as S } from '@/lib/constants/two-factor-strings';

export interface TotpQrCodeProps {
  /** `otpauth://` URI from the setup endpoint. */
  uri: string;
}

type QrState = { status: 'pending' } | { status: 'ready'; dataUrl: string } | { status: 'failed' };

/**
 * QR code for authenticator-app setup (MYN-241), rendered in the browser so
 * the secret never goes to a third-party image service. If rendering fails the
 * failure is reported and the user is pointed at the setup key beside it.
 */
export function TotpQrCode({ uri }: TotpQrCodeProps) {
  const [qr, setQr] = useState<QrState>({ status: 'pending' });

  useEffect(() => {
    let active = true;
    QRCode.toDataURL(uri, { width: 200, margin: 1 })
      .then((dataUrl) => {
        if (active) setQr({ status: 'ready', dataUrl });
      })
      .catch((error: unknown) => {
        // Not silent: reported, and the user is told to use the key instead.
        Sentry.captureException(error);
        if (active) setQr({ status: 'failed' });
      });
    return () => {
      active = false;
    };
  }, [uri]);

  if (qr.status === 'failed') {
    return (
      <p role="alert" className="text-danger m-0 max-w-[200px] text-sm">
        {S.QR_FAILED}
      </p>
    );
  }
  if (qr.status !== 'ready') return null;
  return (
    <img
      src={qr.dataUrl}
      alt={S.QR_ALT}
      width={200}
      height={200}
      className="border-navy/10 rounded-lg border"
    />
  );
}
